function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function roundScore(value) {
    return Math.round(clamp(value, 0, 100));
}

function getFlowerSize(flower) {
    return flower && flower.params && typeof flower.params.size === 'number'
        ? flower.params.size
        : 1;
}

function getFlowerPosition(flower) {
    const position = flower && flower.position ? flower.position : {};
    return {
        x: typeof position.x === 'number' ? position.x : 0,
        y: typeof position.y === 'number' ? position.y : 0,
        z: typeof position.z === 'number' ? position.z : 0,
    };
}

function getStats(flowers) {
    const positions = flowers.map(getFlowerPosition);
    const sizes = flowers.map(getFlowerSize);
    const xs = positions.map(item => item.x);
    const ys = positions.map(item => item.y);
    const zs = positions.map(item => item.z);
    const maxY = Math.max(...ys, 0);
    const minY = Math.min(...ys, 0);
    const maxX = Math.max(...xs, 0);
    const minX = Math.min(...xs, 0);
    const maxZ = Math.max(...zs, 0);
    const minZ = Math.min(...zs, 0);
    const maxSize = Math.max(...sizes, 1);
    const avgSize = sizes.reduce((sum, item) => sum + item, 0) / Math.max(sizes.length, 1);

    return {
        positions,
        sizes,
        maxY,
        minY,
        heightRange: maxY - minY,
        maxX,
        minX,
        xRange: maxX - minX,
        maxZ,
        minZ,
        zRange: maxZ - minZ,
        maxSize,
        avgSize,
        centerX: positions.reduce((sum, item, index) => sum + item.x * sizes[index], 0)
            / Math.max(sizes.reduce((sum, item) => sum + item, 0), 1),
    };
}

function getContainerSize(container) {
    if (!container) {
        return {
            height: 0,
            width: 0,
        };
    }
    const size = container.params && typeof container.params.size === 'number'
        ? container.params.size
        : 1;
    return {
        height: (container.height || 100) * size,
        width: ((container.radius || 70) * 2) * size,
    };
}

function getAverageNearestDistance(positions) {
    if (positions.length < 2) {
        return 0;
    }
    const nearestDistances = positions.map((source, sourceIndex) => {
        let nearest = Infinity;
        positions.forEach((target, targetIndex) => {
            if (sourceIndex === targetIndex) {
                return;
            }
            const dx = source.x - target.x;
            const dy = (source.y - target.y) * 0.35;
            const dz = source.z - target.z;
            nearest = Math.min(nearest, Math.sqrt(dx * dx + dy * dy + dz * dz));
        });
        return nearest;
    });
    return nearestDistances.reduce((sum, item) => sum + item, 0) / nearestDistances.length;
}

function makeMetric(key, name, score, comment) {
    return {
        key,
        name,
        score: roundScore(score),
        comment,
    };
}

function scoreHeightRatio(flowers, container, stats) {
    if (!flowers.length) {
        return makeMetric('heightRatio', '高度比例', 0, '还没有花材，暂时无法判断高度比例。');
    }
    const containerSize = getContainerSize(container);
    if (!containerSize.height) {
        return makeMetric('heightRatio', '高度比例', 58, '缺少器皿，花材高度缺少参照。');
    }
    const ratio = stats.maxY / containerSize.height;
    const distance = Math.abs(ratio - 2.35);
    const score = 100 - distance * 34;
    const comment = ratio < 1.5
        ? '主枝略低，作品气势可以再拉开。'
        : ratio > 3.5
            ? '主枝偏高，器皿压不住整体高度。'
            : '花材高度和器皿比例比较协调。';
    return makeMetric('heightRatio', '高度比例', score, comment);
}

function scoreBalance(flowers, stats) {
    if (flowers.length < 2) {
        return makeMetric('balance', '左右平衡', 60, '花材较少，左右重心还不够稳定。');
    }
    const baseRange = Math.max(stats.xRange, 70);
    const offsetRate = Math.abs(stats.centerX) / baseRange;
    const score = 100 - offsetRate * 165;
    const comment = offsetRate > 0.35
        ? stats.centerX > 0
            ? '整体重心偏右，可以在左侧补充中低位花材。'
            : '整体重心偏左，可以在右侧补充中低位花材。'
        : '左右重心比较稳定。';
    return makeMetric('balance', '左右平衡', score, comment);
}

function scoreLayering(flowers, stats) {
    if (flowers.length < 3) {
        return makeMetric('layering', '层次变化', 55, '花材数量偏少，高低和前后层次还不明显。');
    }
    const heightScore = clamp(stats.heightRange / 160 * 100, 35, 100);
    const depthScore = clamp(stats.zRange / 90 * 100, 30, 100);
    const sizeScore = clamp((stats.maxSize - Math.min(...stats.sizes)) / 0.6 * 100, 35, 100);
    const score = heightScore * 0.45 + depthScore * 0.3 + sizeScore * 0.25;
    const comment = score < 70
        ? '高低、前后或大小变化还可以再拉开。'
        : '作品有一定高低和前后层次。';
    return makeMetric('layering', '层次变化', score, comment);
}

function scoreDensity(flowers, stats) {
    if (!flowers.length) {
        return makeMetric('density', '疏密关系', 0, '还没有花材，暂时无法判断疏密。');
    }
    if (flowers.length === 1) {
        return makeMetric('density', '疏密关系', 52, '单枝作品比较单薄，可以增加辅助花材。');
    }
    const nearest = getAverageNearestDistance(stats.positions);
    const ideal = 85;
    const score = 100 - Math.abs(nearest - ideal) * 0.85;
    const comment = nearest < 45
        ? '花材靠得较近，局部会显得拥挤。'
        : nearest > 150
            ? '花材分布偏散，整体关系可以再收拢。'
            : '花材疏密关系比较自然。';
    return makeMetric('density', '疏密关系', score, comment);
}

function scoreFocus(flowers, stats) {
    if (!flowers.length) {
        return makeMetric('focus', '主次关系', 0, '还没有花材，暂时无法判断主次。');
    }
    const dominance = stats.maxSize / Math.max(stats.avgSize, 0.1);
    const highestBonus = stats.heightRange > 120 ? 12 : 0;
    const countBonus = flowers.length >= 3 ? 8 : 0;
    const score = 55 + (dominance - 1) * 75 + highestBonus + countBonus;
    const comment = score < 72
        ? '主花不够突出，可以放大或抬高一枝作为视觉焦点。'
        : '作品已经有较明确的视觉焦点。';
    return makeMetric('focus', '主次关系', score, comment);
}

function scoreContainerHarmony(flowers, container, stats) {
    if (!container) {
        return makeMetric('containerHarmony', '器皿协调', 48, '缺少器皿，作品落点不够完整。');
    }
    if (!flowers.length) {
        return makeMetric('containerHarmony', '器皿协调', 58, '已有器皿，但还需要花材形成完整作品。');
    }
    const containerSize = getContainerSize(container);
    const widthRatio = Math.max(stats.xRange, 30) / Math.max(containerSize.width, 1);
    const heightRatio = stats.maxY / Math.max(containerSize.height, 1);
    const widthScore = 100 - Math.abs(widthRatio - 0.95) * 45;
    const heightScore = 100 - Math.abs(heightRatio - 2.35) * 30;
    const score = widthScore * 0.45 + heightScore * 0.55;
    const comment = score < 72
        ? '器皿和花材体量还可以再调整，让上下比例更稳。'
        : '器皿和花材体量关系比较协调。';
    return makeMetric('containerHarmony', '器皿协调', score, comment);
}

function getMetricScore(metrics, key, fallback = 0) {
    const metric = metrics.find(item => item.key === key);
    return metric ? metric.score : fallback;
}

function scoreSkillLevel(flowers, metrics) {
    if (!flowers.length) {
        return makeMetric('skillLevel', '技能等级', 0, '还没有花材，暂时无法判断操作技能。');
    }
    const score = getMetricScore(metrics, 'heightRatio') * 0.18
        + getMetricScore(metrics, 'balance') * 0.18
        + getMetricScore(metrics, 'layering') * 0.24
        + getMetricScore(metrics, 'density') * 0.14
        + getMetricScore(metrics, 'focus') * 0.16
        + getMetricScore(metrics, 'containerHarmony') * 0.1;
    const comment = score < 60
        ? '基础操作还需练习，建议先加强比例、层次和主次控制。'
        : score < 80
            ? '已掌握基本造型技能，细节控制仍有提升空间。'
            : '比例、层次与重心控制较成熟，技能完成度较高。';
    return makeMetric('skillLevel', '技能等级', score, comment);
}

function scoreInnovation(flowers, stats) {
    if (!flowers.length) {
        return makeMetric('innovation', '创新性', 0, '还没有花材，暂时无法判断创新表达。');
    }
    const resourceCount = new Set(flowers.map(item => item.resourceId || item.resourceName)).size;
    const diversityScore = clamp(resourceCount / Math.min(Math.max(flowers.length, 1), 5) * 100, 20, 100);
    const sizeRange = stats.maxSize - Math.min(...stats.sizes);
    const sizeVariationScore = clamp(sizeRange / 0.8 * 100, 20, 100);
    const spatialScore = (
        clamp(stats.heightRange / 220 * 100, 20, 100)
        + clamp(stats.xRange / 180 * 100, 20, 100)
        + clamp(stats.zRange / 120 * 100, 20, 100)
    ) / 3;
    const score = diversityScore * 0.45 + sizeVariationScore * 0.2 + spatialScore * 0.35;
    const comment = score < 60
        ? '素材和造型变化较少，可以尝试新的组合或更大胆的空间关系。'
        : score < 80
            ? '作品已有一定变化，可进一步强化独特的主题或造型语言。'
            : '素材组合与空间变化较丰富，作品具有较鲜明的创意表达。';
    return makeMetric('innovation', '创新性', score, comment);
}

function scoreApplicationValue(flowers, container, metrics) {
    if (!flowers.length) {
        return makeMetric('applicationValue', '应用价值', container ? 25 : 0, '作品尚未完成，暂时不具备实际展示或应用条件。');
    }
    const stabilityScore = getMetricScore(metrics, 'balance') * 0.3
        + getMetricScore(metrics, 'density') * 0.2
        + getMetricScore(metrics, 'focus') * 0.2
        + getMetricScore(metrics, 'containerHarmony') * 0.3;
    const completenessScore = clamp(flowers.length / 6 * 100, 25, 100) * 0.75
        + (container ? 25 : 0);
    const score = stabilityScore * 0.7 + completenessScore * 0.3;
    const comment = score < 60
        ? '作品完整度或稳定性不足，实际展示前还需要继续调整。'
        : score < 80
            ? '作品具备基本展示价值，完善器皿与细节后适用性会更强。'
            : '作品结构完整、视觉稳定，具备较好的展示与场景应用价值。';
    return makeMetric('applicationValue', '应用价值', score, comment);
}

function scoreProfessionalAesthetics(flowers, metrics) {
    if (!flowers.length) {
        return makeMetric('professionalAesthetics', '专业性美学', 0, '还没有花材，暂时无法进行专业美学判断。');
    }
    const score = getMetricScore(metrics, 'heightRatio') * 0.15
        + getMetricScore(metrics, 'balance') * 0.2
        + getMetricScore(metrics, 'layering') * 0.22
        + getMetricScore(metrics, 'density') * 0.15
        + getMetricScore(metrics, 'focus') * 0.18
        + getMetricScore(metrics, 'containerHarmony') * 0.1;
    const comment = score < 60
        ? '比例、层次与主次关系仍需统一，专业美感有待加强。'
        : score < 80
            ? '整体审美关系基本成立，局部比例与节奏还可继续精修。'
            : '比例、节奏、主次与器皿关系协调，专业美学完成度较高。';
    return makeMetric('professionalAesthetics', '专业性美学', score, comment);
}

function getSummary(totalScore) {
    if (totalScore >= 85) {
        return '整体完成度较高，结构稳定，已经有较好的展示效果。';
    }
    if (totalScore >= 70) {
        return '作品基础关系成立，继续微调层次和重心会更完整。';
    }
    if (totalScore >= 55) {
        return '作品已有雏形，但比例、层次或主次关系还需要加强。';
    }
    return '作品信息还不够完整，建议先补充花材、器皿和基本层次。';
}

function getSuggestions(metrics) {
    const weakMetrics = metrics
        .filter(item => item.score < 75)
        .sort((a, b) => a.score - b.score)
        .slice(0, 3);
    if (!weakMetrics.length) {
        return ['整体关系较完整，可以尝试从色彩和主题表达上继续提升。'];
    }
    return weakMetrics.map(item => item.comment);
}

function scoreArtwork(jsonData = {}) {
    const objects = jsonData.objects || {};
    const flowers = objects.flowers || [];
    const container = objects.container || (objects.containers && objects.containers[0]) || null;
    const stats = getStats(flowers);
    const basicMetrics = [
        scoreHeightRatio(flowers, container, stats),
        scoreBalance(flowers, stats),
        scoreLayering(flowers, stats),
        scoreDensity(flowers, stats),
        scoreFocus(flowers, stats),
        scoreContainerHarmony(flowers, container, stats),
    ];
    const metrics = [
        ...basicMetrics,
        scoreSkillLevel(flowers, basicMetrics),
        scoreInnovation(flowers, stats),
        scoreApplicationValue(flowers, container, basicMetrics),
        scoreProfessionalAesthetics(flowers, basicMetrics),
    ];
    const totalScore = roundScore(
        metrics.reduce((sum, item) => sum + item.score, 0) / metrics.length
    );
    return {
        totalScore,
        metrics,
        suggestions: getSuggestions(metrics),
        summary: getSummary(totalScore),
    };
}

module.exports = {
    scoreArtwork,
};
